/**
 * Runtime backend URL resolver.
 *
 * Problem
 * -------
 * `REACT_APP_BACKEND_URL` is baked into the CRA bundle at build time. When a
 * custom production domain is bound (e.g. `acf.acfjourneys.online`) the baked
 * URL still points to the Emergent-provided preview/prod URL
 * (`*.emergentagent.com` / `*.deploy.emergentcf.cloud`). Browsers therefore
 * treat all /api/* XHRs as cross-site requests, which means 3rd-party cookies
 * get blocked by default in Chrome / Safari / Firefox — so login works but
 * every follow-up data call returns 401.
 *
 * Solution
 * --------
 * At runtime, rewrite the baked-in backend URL to the current origin whenever
 * the page is served from any hostname other than localhost. The Emergent
 * kubernetes ingress forwards `/api/*` from the user-facing domain to the
 * backend, so same-origin XHRs "just work" — no CORS, no cross-site cookies.
 *
 * The rewrite is applied globally via axios + fetch interceptors installed
 * once at app boot — see `src/utils/installApiInterceptor.js`.
 */

/** Base URL baked into the bundle at build time. Trailing slashes trimmed. */
export const ENV_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

/** The origin that should actually be used for /api/* calls right now. */
export function getRuntimeApiBase() {
  if (typeof window === 'undefined') return ENV_BACKEND_URL;
  const host = window.location.hostname;
  // Local dev — frontend on :3000, backend on remote/localhost:8001.
  // Keep using the explicit env URL so dev is unaffected.
  if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
    return ENV_BACKEND_URL;
  }
  // Everywhere else (preview + custom production domain) — same-origin,
  // because Emergent's k8s ingress routes /api/* to the backend.
  return window.location.origin;
}

/**
 * Rewrite an absolute URL that points at the bundled ENV_BACKEND_URL so it
 * hits the runtime origin instead. Relative URLs and unrelated absolute URLs
 * pass through unchanged.
 */
export function rewriteApiUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const runtimeBase = getRuntimeApiBase();
  if (!runtimeBase || runtimeBase === ENV_BACKEND_URL) return url;
  if (ENV_BACKEND_URL && url.startsWith(ENV_BACKEND_URL)) {
    return runtimeBase + url.slice(ENV_BACKEND_URL.length);
  }
  return url;
}
